import { matchRoutes, useLocation } from 'react-router-dom';
import { useAppSelector } from '@app/redux/hooks';
import {
  getFilteredSideDrawerItems,
  SideMenuItem,
  sideDrawerItems,
} from '@app/utils/consts';
import { getBusinessPlan } from '@app/utils/utils';
import HorizontalLogo from '@app/assets/horizontal-logo.svg?react';
import SettingsIcon from '@app/assets/icons/menu/settings.svg?react';
import DotsVerticalIcon from '@app/assets/icons/menu/dot-vertical.svg?react';
import ArrowDownRightIcon from '@app/assets/icons/arrow-down-right-sm.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import { useNavigationContext } from '@app/contexts/NavigationContext';
import { Button } from '@app/components/Button/Button';
import { getBusinessInfo } from '@app/services/business';
import { Modal } from '@app/components/Modal/Modal';
import { LeaveFeedback } from '@app/pages/LeaveFeedback/LeaveFeedback';
import { isUserAdmin } from '@app/utils/user';

export const SideDrawer = () => {
  const [expandedSubItem, setExpandedSubItem] = useState<number | undefined>();
  const [businessDetails, setBusinessDetails] = useState<any>(null);
  const [isLoadingBusiness, setIsLoadingBusiness] = useState(false);
  const [isLeaveFeedbackModalOpen, setIsLeaveFeedbackModalOpen] =
    useState(false);
  const businessState = useAppSelector((state) => state.business);
  const location = useLocation();
  const { interceptNavigation } = useNavigationContext();

  // Get current business plan type
  const currentBusinessId = businessState?.selected?.id;
  const businessPlan = currentBusinessId
    ? getBusinessPlan(currentBusinessId)
    : null;
  const planType = businessPlan?.type;

  // Fetch business details to get the logo
  const fetchBusinessDetails = async () => {
    if (!businessDetails && !isLoadingBusiness && currentBusinessId) {
      setIsLoadingBusiness(true);
      try {
        const response = await getBusinessInfo();
        setBusinessDetails(response?.data);
      } catch (error) {
        console.error('Error fetching business details:', error);
      } finally {
        setIsLoadingBusiness(false);
      }
    }
  };

  // Fetch business details when component mounts or business changes
  useEffect(() => {
    fetchBusinessDetails();
  }, [currentBusinessId, businessDetails, isLoadingBusiness]);

  // Get filtered menu items based on plan and admin status (memoized to prevent unnecessary re-renders)
  const authState = useAppSelector((state) => state.auth);
  const filteredSideDrawerItems = useMemo(() => {
    const isAdmin = authState?.user && isUserAdmin(authState.user.role);
    return getFilteredSideDrawerItems(planType, isAdmin);
  }, [planType, authState?.user]);

  useEffect(() => {
    if (location.pathname) {
      // Use original sideDrawerItems to find the correct index, then map to filtered items
      const originalIndex = sideDrawerItems.findIndex((item) => {
        const childrenPaths =
          item.children?.map((child) => ({
            path: child.to,
          })) ?? [];

        return matchRoutes(
          [...childrenPaths, { path: item.to }],
          location.pathname
        );
      });

      // Find the corresponding index in filtered items
      if (originalIndex !== -1) {
        const originalItem = sideDrawerItems[originalIndex];
        const filteredIndex = filteredSideDrawerItems.findIndex(
          (item) => item.label === originalItem.label
        );
        setExpandedSubItem(filteredIndex !== -1 ? filteredIndex : undefined);
      } else {
        setExpandedSubItem(undefined);
      }
    }
  }, [location.pathname, filteredSideDrawerItems]);

  return (
    <div className="bg-white h-full  flex flex-col items-start border-r">
      <div className="border-b border-b-base-ghostWhite p-4 w-full">
        <HorizontalLogo width={126} height={56} />
      </div>
      <div className="border-b border-b-base-ghostWhite p-4 w-full">
        <div className="flex items-center justify-between gap-2">
          <div className="w-8 h-8 rounded-lg shrink-0 overflow-hidden bg-gray-100 flex items-center justify-center">
            {businessDetails?.photo?.url ? (
              <img
                src={businessDetails.photo.url}
                alt={`${businessState?.selected?.name} logo`}
                className="w-full h-full object-cover"
              />
            ) : isLoadingBusiness ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
            ) : (
              <span className="font-bold text-lg text-gray-600">
                {businessState?.selected?.name?.charAt(0)}
              </span>
            )}
          </div>
          <h5 className="font-bold text-sm mr-auto break-all">
            {businessState?.selected?.name}
          </h5>
          <button
            onClick={() => interceptNavigation('/select-business')}
            className="shrink-0"
          >
            <DotsVerticalIcon />
          </button>
        </div>
      </div>
      <div className="w-full relative">
        <ul className="flex gap-4 flex-col text-sm">
          {filteredSideDrawerItems.map((item: SideMenuItem, index: number) => (
            <li key={index}>
              {item.children && item.children.length > 0 ? (
                <span
                  onClick={() => {
                    if (!item.comingSoon) {
                      setExpandedSubItem(
                        expandedSubItem === index ? undefined : index
                      );
                    }
                  }}
                  className={clsx(
                    'flex gap-2 items-center px-4 py-2 rounded-lg',
                    {
                      'font-bold bg-brand-baseBlue':
                        matchRoutes([{ path: item.to }], location.pathname) &&
                        !item.children?.some((child) =>
                          matchRoutes([{ path: child.to }], location.pathname)
                        ),
                      'hover:bg-brand-lightBlue4 cursor-pointer':
                        !item.comingSoon,
                      'cursor-not-allowed opacity-50': item.comingSoon,
                    }
                  )}
                >
                  {item.Icon && <item.Icon />}
                  <span className="flex-1">{item.label}</span>
                  {item.comingSoon && (
                    <span className="text-xs text-base-davyGray">
                      (Coming soon)
                    </span>
                  )}
                </span>
              ) : item.comingSoon ? (
                <div className="flex gap-2 items-center px-4 py-2 rounded-lg">
                  {item.Icon && <item.Icon />}
                  <span className="flex-1 opacity-50">{item.label}</span>
                  <Button
                    size="small"
                    className="text-xs px-1 py-0.5 min-w-fit h-5 text-xs bg-brand-baseBlue text-white cursor-default"
                  >
                    Coming soon
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => interceptNavigation(item.to)}
                  className={clsx(
                    'flex relative gap-2 items-center px-4 py-2 rounded-lg w-full text-left',
                    {
                      'bg-brand-lightBlue4 text-brand-baseBlue font-bold side-tab':
                        matchRoutes([{ path: item.to }], location.pathname),
                      'hover:bg-brand-lightBlue4 cursor-pointer': true,
                    }
                  )}
                >
                  {item.Icon && <item.Icon />}
                  <span className="flex-1">{item.label}</span>
                </button>
              )}

              {item.children &&
                item.children?.length > 0 &&
                expandedSubItem === index && (
                  <ul>
                    {item.children?.map(
                      (subItem: SideMenuItem, subIndex: number) => (
                        <li key={subIndex}>
                          {subItem.comingSoon ? (
                            <div className="relative flex gap-2 items-center px-4 py-2 rounded-lg w-full text-left">
                              {subItem.Icon && <subItem.Icon />}
                              <span className="ml-10 flex-1 opacity-50">
                                {subItem.label}
                              </span>
                              <Button
                                size="small"
                                className="text-xs px-1 py-0.5 min-w-fit h-5 text-xs bg-brand-baseBlue text-white cursor-default"
                              >
                                Coming soon
                              </Button>
                            </div>
                          ) : (
                            <button
                              onClick={() => interceptNavigation(subItem.to)}
                              className={clsx(
                                'relative flex gap-2 items-center px-4 py-2 rounded-lg w-full text-left',
                                {
                                  'font-bold bg-brand-lightBlue4 text-brand-baseBlue side-tab':
                                    matchRoutes(
                                      [{ path: subItem.to }],
                                      location.pathname
                                    ),
                                  'hover:bg-brand-lightBlue4 cursor-pointer': true,
                                }
                              )}
                            >
                              {subItem.Icon && <subItem.Icon />}
                              <span className="ml-10 flex-1">
                                {subItem.label}
                              </span>
                            </button>
                          )}
                        </li>
                      )
                    )}
                  </ul>
                )}
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto p-6">
        <button
          onClick={() => setIsLeaveFeedbackModalOpen(true)}
          className="w-full"
        >
          <div className="bg-system-successLight p-3 rounded-lg mb-2 hover:bg-system-successLight/80 transition-colors cursor-pointer">
            <h5 className="text-system-successDark text-sm font-bold mb-1 flex items-center justify-between">
              Leave feedback
              <ArrowDownRightIcon />
            </h5>
            <p className="text-xs text-base-davyGray">
              Leave feedback on our platform so we can make it better
            </p>
          </div>
        </button>
        <div>
          <button
            onClick={() => interceptNavigation('/settings')}
            className="flex gap-2 mb-2 items-center hover:bg-brand-lightBlue4 p-1 rounded-lg w-full text-left"
          >
            <SettingsIcon />
            Settings
          </button>
        </div>
      </div>

      <Modal
        open={isLeaveFeedbackModalOpen}
        onClose={() => setIsLeaveFeedbackModalOpen(false)}
        size="max-w-4xl"
      >
        <div className="relative">
          <button
            onClick={() => setIsLeaveFeedbackModalOpen(false)}
            className="absolute top-4 right-4 z-10 p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <CloseIcon />
          </button>
          <LeaveFeedback />
        </div>
      </Modal>
    </div>
  );
};
